import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, SITE } from "@/data/site";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/Kinetic";
import { CtaLink } from "@/components/common/Buttons";
import { Reveal, SectionHead } from "@/components/common/Reveal";
import { FinalCta } from "@/components/home/FinalCta";
import NotFound from "@/pages/NotFound";

const List = ({ title, items, testId }) => (
  <Reveal className="border-t border-slate-200 pt-8" data-testid={testId}>
    <h3 className="eyebrow text-slate-500">{title}</h3>
    <ul className="mt-6 space-y-4">
      {items.map((t) => (
        <li key={t} className="flex gap-3 text-[15px] leading-relaxed text-slate-700">
          <Check className="mt-1 h-4 w-4 shrink-0 text-blue-600" />{t}
        </li>
      ))}
    </ul>
  </Reveal>
);

const Others = ({ current }) => (
  <section className="bg-[#f8fafc] py-20" data-testid="other-services">
    <div className="container-q">
      <h2 className="text-2xl font-extrabold tracking-tight text-slate-950">Other services</h2>
      <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
        {SERVICES.filter((s) => s.slug !== current).map((s) => {
          const Icon = s.icon;
          return (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group flex flex-col gap-6 bg-white p-6 transition-colors hover:bg-slate-50" data-testid={`other-service-${s.key}`}>
              <Icon className="h-5 w-5 text-blue-600" />
              <span className="flex items-end justify-between gap-3 text-[15px] font-bold leading-snug text-slate-950">{s.title}<ArrowUpRight className="h-4 w-4 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <NotFound />;
  const Icon = s.icon;
  return (
    <>
      <Seo path={`/services/${s.slug}`} title={`${s.title} | Quantivo`} description={`${s.intro.slice(0, 150).replace(/\s+\S*$/, "")}.`} />
      <PageHero testId="service-hero" eyebrow={`Service · ${s.title}`} lines={[s.title]} text={s.intro}>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <CtaLink to="/contact" state={{ service: s.title }} data-testid="service-hero-cta">Discuss Your Project</CtaLink>
          <CtaLink href={`mailto:${SITE.email}?subject=${encodeURIComponent(`Enquiry: ${s.title}`)}`} variant="ghostDark" arrow="right" data-testid="service-hero-email">Email Quantivo</CtaLink>
        </div>
      </PageHero>
      <section className="bg-white py-24 lg:py-28">
        <div className="container-q grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-slate-950 text-cyan-300"><Icon className="h-6 w-6" /></span>
            <SectionHead className="mt-8" index="01" label="Capabilities" title="What this covers" />
          </div>
          <div className="grid gap-12 sm:grid-cols-2 lg:col-span-8">
            <List title="Core capabilities" items={s.bullets} testId="service-capabilities" />
            <List title="A good fit for" items={s.idealFor} testId="service-ideal-for" />
            <List title="Typical deliverables" items={s.deliverables} testId="service-deliverables" />
            <List title="How we approach it" items={s.notes} testId="service-approach" />
          </div>
        </div>
      </section>
      <Others current={s.slug} />
      <FinalCta />
    </>
  );
}
